import type { RagImageItem } from "../types/ragChatTypes";
import {
  buildStepImagesByIndex,
  classifyNumberedAnswerKind,
  cleanStepDisplayText,
  looksLikeStandaloneAnswerHeading,
  parseNumberedSteps,
  stripInlineMarkdown,
  type ParsedStep,
  type StepImageBinding,
} from "./ragChatUtils";

/** One numbered callout in an exploded-view diagram legend. */
export interface CatalogLegendItem {
  number: number;
  name: string;
  description?: string;
  page?: string;
  images: StepImageBinding[];
}

/** Structured Parts Catalog layout sections (original API order). */
export interface PartsCatalogPresentation {
  show: boolean;
  entries: CatalogLegendItem[];
  /** Servicing notes / preamble before the component legend. */
  introMarkdown: string;
  /** Heading for the catalog block (rendered with the catalog, not in intro). */
  catalogHeadingMarkdown: string | null;
  /** Procedure / notes sections after the component legend. */
  postMarkdown: string;
}

const PROCEDURE_QUERY_RE =
  /\b(?:how\s+to|steps?\s+(?:for|to)|procedure\s+(?:for|to)|removing|installing|replacing|remove|install|replace|disassembl|overhaul|give\s+me\s+(?:the\s+)?steps)\b/i;

const PARTS_QUERY_PATTERNS: RegExp[] = [
  /\bcomponents?\s+(?:of|in|shown|for|that)\b/i,
  /\bparts?\s+(?:of|in|list|catalog|shown|for|that)\b/i,
  /\blist\s+(?:of\s+)?(?:the\s+)?(?:parts?|components?)\b/i,
  /\bwhat\s+are\s+the\s+(?:components?|parts?)\b/i,
  /\bshow\s+(?:me\s+)?(?:the\s+)?(?:parts?|components?)\b/i,
  /\bidentify\s+the\s+(?:parts?|components?)\b/i,
  /\bcomponents?\s+in\s+this\s+diagram\b/i,
  /\blist\s+the\s+components?\b/i,
  /\bshow\s+all\s+parts?\b/i,
  /\bassembly\s+(?:parts?|components?)\b/i,
  // Diagram-legend answers often come from "specifications for/of …" or
  // "part number" queries. Answer-shape gates (dash markers +
  // MIN_CATALOG_ITEMS_FOR_UI) still apply.
  /\bwhat\s+are\s+the\s+(?:specifications?|specs?)\b/i,
  /\b(?:specifications?|specs?)\s+(?:for|of|on)\b/i,
  /\bpart\s+numbers?\b/i,
];

/** Minimum legend entries required before showing the structured UI. */
export const MIN_CATALOG_ITEMS_FOR_UI = 3;

/** Exact synthesizer refusal line sometimes appended after useful content. */
export const NO_INFO_FALLBACK_LINE = "No information available for this query.";

/** Detect user intent for a parts/components catalog query (not procedures). */
export function isPartsCatalogQuery(query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  if (PROCEDURE_QUERY_RE.test(q)) return false;
  return PARTS_QUERY_PATTERNS.some((re) => re.test(q));
}

/** Split a catalog line into display name and optional subtitle. */
export function parseCatalogItemFields(text: string): { name: string; description?: string } {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return { name: "" };

  const colonIdx = clean.indexOf(":");
  if (colonIdx > 0 && colonIdx <= 60) {
    const name = clean.slice(0, colonIdx).trim();
    const description = clean.slice(colonIdx + 1).trim();
    return { name, description: description || undefined };
  }

  const torqueMatch = clean.match(
    /^(.+?)\s+(\d+(?:\.\d+)?\s*(?:N[·\s]?m|Nm|N\.m|ft[-\s]?lb))\s*$/i
  );
  if (torqueMatch && torqueMatch[1].trim().length >= 3) {
    return {
      name: torqueMatch[1].trim(),
      description: torqueMatch[2].trim(),
    };
  }

  return { name: clean };
}

/**
 * Parse numbered dash-list entries as diagram legend callouts.
 * When `indexAllowList` is set, only those step indices are considered
 * (contiguous legend run) so later procedure content is not absorbed.
 *
 * Catalog row thumbnails are attached only when the part text explicitly cites
 * an image (`[p.N]` / `[img:N]`). Proportional fallback from
 * {@link buildStepImagesByIndex} must not invent associations for uncited parts.
 */
export function parseCatalogLegendFromSteps(
  steps: ParsedStep[],
  stepImagesByIndex: Record<number, StepImageBinding[]>,
  indexAllowList?: ReadonlySet<number>
): CatalogLegendItem[] {
  const entries: CatalogLegendItem[] = [];

  steps.forEach((step, index) => {
    if (indexAllowList && !indexAllowList.has(index)) return;
    if (!step.num) return;
    if (step.marker !== "dash") return;

    const num = Number.parseInt(step.num, 10);
    if (!Number.isFinite(num)) return;

    const { name, description } = parseCatalogItemFields(step.text);
    if (!name) return;

    const pageMatch = step.text.match(/\[p\.(\d+)\]/);
    const hasExplicitImageRef =
      Boolean(pageMatch) || /\[img:\d+\]/.test(step.text);
    entries.push({
      number: num,
      name,
      description,
      page: pageMatch?.[1],
      images: hasExplicitImageRef ? stepImagesByIndex[index] ?? [] : [],
    });
  });

  return entries.sort((a, b) => a.number - b.number);
}

export function shouldUsePartsCatalogView(
  userQuery: string | undefined,
  _numberedKind: "procedure" | "spec-list" | null,
  entries: CatalogLegendItem[]
): boolean {
  if (!isPartsCatalogQuery(userQuery ?? "")) return false;
  if (entries.length < MIN_CATALOG_ITEMS_FOR_UI) return false;
  // Parts/components queries with a parsed dash legend take precedence over
  // procedure Step Cards. Classification kind is intentionally unused here.
  return true;
}

function isDashLegendStep(step: ParsedStep): boolean {
  return Boolean(step.num && step.marker === "dash");
}

/** Contiguous run of dash-marker numbered legend lines. */
export function findContiguousDashLegendRange(
  steps: ParsedStep[]
): { start: number; end: number } | null {
  let start = -1;
  for (let i = 0; i < steps.length; i++) {
    if (isDashLegendStep(steps[i]!)) {
      start = i;
      break;
    }
  }
  if (start < 0) return null;

  let end = start;
  for (let i = start + 1; i < steps.length; i++) {
    if (isDashLegendStep(steps[i]!)) {
      end = i;
      continue;
    }
    break;
  }
  return { start, end };
}

function formatHeadingMarkdown(line: string): string {
  const plain = stripInlineMarkdown(line)
    .replace(/^#{1,4}\s+/, "")
    .replace(/:+$/, "")
    .trim();
  return plain ? `**${plain}**` : line;
}

/**
 * Format a slice of parsed steps as Markdown prose (headings restored).
 * Numbered non-dash steps are preserved as ordered lines.
 *
 * Keeps `[p.N]` / `[img:N]` markers so post-catalog procedure UI can bind
 * inline images. Strip citations only at the final display layer
 * ({@link cleanStepDisplayText}).
 */
export function formatPartsCatalogRegionMarkdown(
  steps: Array<Pick<ParsedStep, "num" | "text" | "marker">>
): string {
  return steps
    .map((step) => {
      const raw = String(step.text ?? "").trim();
      // Heading classification ignores citation markers; body keeps them.
      const forHeading = cleanStepDisplayText(raw);
      if (!raw && !step.num) return "";
      if (step.num && step.marker === "dash") {
        return `${step.num} - ${raw}`.trim();
      }
      if (step.num) {
        const sep = step.marker === "paren" ? ")" : ".";
        return `${step.num}${sep} ${raw}`.trim();
      }
      if (looksLikeStandaloneAnswerHeading(forHeading)) {
        return formatHeadingMarkdown(forHeading);
      }
      return raw;
    })
    .filter(Boolean)
    .join("\n\n");
}

/**
 * Build Markdown intro from unnumbered steps only (legacy helper).
 * Prefer {@link evaluatePartsCatalogPresentation} section fields for layout.
 */
export function formatPartsCatalogIntroMarkdown(
  steps: Array<Pick<ParsedStep, "num" | "text" | "marker">>
): string {
  return formatPartsCatalogRegionMarkdown(steps.filter((step) => !step.num));
}

/**
 * Drop a trailing synthesizer refusal line when the answer already has content.
 * Does not remove the line when it is the entire answer.
 */
export function stripTrailingNoInfoFallback(markdown: string): string {
  const raw = String(markdown ?? "").replace(/\r\n/g, "\n").trim();
  if (!raw) return "";
  if (raw === NO_INFO_FALLBACK_LINE) return raw;

  const parts = raw.split(/\n\n+/);
  const last = (parts[parts.length - 1] ?? "").trim();
  if (last !== NO_INFO_FALLBACK_LINE) return raw;
  if (parts.length < 2) return raw;
  return parts.slice(0, -1).join("\n\n").trim();
}

function emptyPresentation(): PartsCatalogPresentation {
  return {
    show: false,
    entries: [],
    introMarkdown: "",
    catalogHeadingMarkdown: null,
    postMarkdown: "",
  };
}

/**
 * Classify + split a parts-catalog answer into intro / catalog / post sections
 * so the component legend heading stays with the catalog UI.
 */
export function evaluatePartsCatalogPresentation(
  userQuery: string | undefined,
  answerText: string,
  inlineImages: Record<string, RagImageItem[]>
): PartsCatalogPresentation {
  const steps = parseNumberedSteps(answerText);
  if (!steps?.some((s) => s.num)) {
    return emptyPresentation();
  }

  const range = findContiguousDashLegendRange(steps);
  if (!range) {
    return emptyPresentation();
  }

  const allow = new Set<number>();
  for (let i = range.start; i <= range.end; i++) allow.add(i);

  const numberedKind = classifyNumberedAnswerKind(steps);
  const stepImagesByIndex = buildStepImagesByIndex(steps, inlineImages);
  const entries = parseCatalogLegendFromSteps(steps, stepImagesByIndex, allow);
  const show = shouldUsePartsCatalogView(userQuery, numberedKind, entries);
  if (!show) {
    return emptyPresentation();
  }

  let headingIdx = -1;
  const beforeLegend = range.start - 1;
  if (beforeLegend >= 0) {
    const candidate = steps[beforeLegend]!;
    if (
      !candidate.num &&
      looksLikeStandaloneAnswerHeading(cleanStepDisplayText(candidate.text))
    ) {
      headingIdx = beforeLegend;
    }
  }

  const introSteps =
    headingIdx >= 0 ? steps.slice(0, headingIdx) : steps.slice(0, range.start);
  const headingStep = headingIdx >= 0 ? steps[headingIdx]! : null;
  const postSteps = steps.slice(range.end + 1);

  const catalogHeadingMarkdown = headingStep
    ? formatHeadingMarkdown(cleanStepDisplayText(headingStep.text))
    : null;

  return {
    show: true,
    entries,
    introMarkdown: formatPartsCatalogRegionMarkdown(introSteps),
    catalogHeadingMarkdown,
    postMarkdown: stripTrailingNoInfoFallback(
      formatPartsCatalogRegionMarkdown(postSteps)
    ),
  };
}
